"use client";
import FormControl, { type FormControlProps } from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel, { formLabelClasses } from "@mui/material/FormLabel";
import Stack from "@mui/material/Stack";
import { type ReactNode } from "react";
import InfoAdornment from "@/components/common/formElements/library/InfoAdornment";
import { StyledInlineLayout } from "./styles";

export interface FormFieldWrapperProps extends FormControlProps {
  label?: ReactNode;
  helperText?: ReactNode;
  errorMessage?: ReactNode;
  inline?: boolean;
  testId?: string;
  tooltip?: ReactNode;
}

export default function FormFieldWrapper(props: FormFieldWrapperProps) {
  const {
    label,
    children,
    helperText,
    error,
    errorMessage,
    fullWidth = true,
    inline,
    testId = "form-field-wrapper",
    tooltip,
    sx,
    ...formFieldWrapperProps
  } = props;

  const labelContent = label && (
    <Stack alignItems="center" direction="row">
      <FormLabel>{label}</FormLabel>
      {tooltip && (
        <InfoAdornment
          tooltip={tooltip}
          inputAdornmentProps={{ sx: { m: 0 } }}
        />
      )}
    </Stack>
  );

  const helperTextContent = helperText && (
    <FormHelperText>{helperText}</FormHelperText>
  );

  const errorMessageContent = error && errorMessage && (
    <FormHelperText>{errorMessage}</FormHelperText>
  );

  return (
    <FormControl
      error={error}
      fullWidth={fullWidth}
      data-testid={testId}
      sx={{
        [`& .${formLabelClasses.root}`]: {
          color: "inherit",
        },
        ...sx,
      }}
      {...formFieldWrapperProps}
    >
      {inline ? (
        <>
          <StyledInlineLayout>
            {labelContent}
            {children}
          </StyledInlineLayout>
          {helperTextContent}
          {errorMessageContent}
        </>
      ) : (
        <>
          {(label || tooltip || helperText) && (
            <div>
              {labelContent}
              {helperTextContent}
            </div>
          )}
          {children}
          {errorMessageContent}
        </>
      )}
    </FormControl>
  );
}
